import React, { RefObject, useEffect, useState } from 'react';
import Menu, { SubMenu, Item as MenuItem } from 'rc-menu';
import 'rc-menu/assets/index.css';
import { createGlobalStyle } from 'styled-components';
import { MenuMode } from 'rc-menu/es/interface';
import { HeaderMenu, MenuItem as HeaderMenuItem } from './HeaderBlock';

const GlobalStyle = createGlobalStyle`
    .rc-menu,
    .rc-menu-submenu {
        z-index: 555;
    }
    
    .rc-menu-horizontal > .rc-menu-item {
      padding: 0px !important;
    }

    .rc-menu-item > a,
    .sl-menu > li {
        color: ${(props: Props) => props.menuFontColor};
    }

    .rc-menu-submenu > li {
        color: ${(props: Props) => props.menuFontColor};
    }
    
    /* React components */
    .rc-menu,
    .rc-menu-item,
    .rc-menu-submenu,
    .rc-menu span,
    .rc-menu a {
        /* Same as normal bold text */
        font-style: normal;
        font-size: 16px;
        font-family: Inter,-apple-system,BlinkMacSystemFont,system-ui,sans-serif;
        -webkit-font-smoothing: auto !important;
        line-height: 20px;
        letter-spacing: normal;
    }

    .rc-menu-submenu > .rc-menu {
        background-color: ${(props: Props) =>
          props.menuDropdownBackgroundColor} !important;
    }
    
    .rc-menu-horizontal > .rc-menu-submenu-active > .rc-menu-submenu-title,
    .rc-menu-horizontal > .rc-menu-submenu-active > .rc-menu-submenu-title span,
    .rc-menu-horizontal > .rc-menu-submenu-active span,
    .rc-menu-horizontal > .rc-menu-item-active a {
        color: ${(props: Props) => props.menuFontHoverColor} !important;
    }
    
    
    .rc-menu-horizontal > .rc-menu-submenu > .rc-menu-submenu-title {
        padding: 0px 12px !important;
    }
    
    .sl-menu a,
     rc-menu-submenu-title {
        text-decoration: none; 
        padding: 0px 12px !important;
    }
    
    .rc-menu-submenu-popup {
      border-radius: 8px;
      box-shadow: 0px 8px 16px rgba(18, 32, 59, 0.12), 0px 4px 12px rgba(18, 32, 59, 0.16);
    }
    
    .rc-menu-sub {
      overflow: hidden;
      border-radius: 8px !important;
    }
    
    .rc-menu-submenu-title:hover,
    .rc-menu-item:hover {
      text-decoration-line: underline;
    }
    
    .header-rc-menu-popup li,
    .header-rc-menu-popup a {
        color: ${(props: Props) => props.menuDropdownFontColor} !important;
    }
    
    .header-rc-menu-popup span:hover,
    .header-rc-menu-popup a:hover,
    .header-rc-menu-popup li:hover,
    .header-rc-menu-popup div:hover {
      background-color: ${(props: Props) =>
        props.menuItemHoverColor} !important;
        color: ${(props: Props) => props.menuDropdownFontHoverColor} !important;
    }
    
    .header-rc-menu-item {
      text-decoration-line: underline;
    }
    
    // HORIZONTAL
        
    .rc-menu-submenu .rc-menu-inline {
        margin-left: 20px;
    }
    
    .rc-menu-inline > .rc-menu-item {
        padding-left: 0 !important;
    }

    .rc-menu-vertical > .rc-menu-item {
        padding: 0px !important
    }

    .rc-menu-inline > .rc-menu-submenu > .rc-menu-submenu-title {
        padding: 12px 20px !important;
    }

    // rc menu adds and empty overflow element after each item so to target the last visible item we need to select the 2nd last item.
    .rc-menu-horizontal > .rc-menu-item:nth-last-child(2) > a,
    .rc-menu-horizontal > .rc-menu-submenu:nth-last-child(2) > .rc-menu-submenu-title {
        padding-right: 0 !important;
    }

    .rc-menu-submenu .rc-menu-item a {
        padding: 12px 20px !important;
        display: block;
    }

    .rc-menu-sub.rc-menu-inline > .rc-menu-item {
        padding: 0 !important;
    }

    .rc-menu-horizontal > .rc-menu-submenu, .rc-menu-horizontal > .rc-menu-item {
        vertical-align: baseline !important;
        border: none !important;
    }    
    
    .rc-menu-horizontal .rc-menu-submenu-arrow {
        margin-left: 8px;
        margin-right: 2px;
        display: inline-block;
        transform: rotate(90deg);
        transition: transform 0.3s;
    }
        
    .rc-menu-horizontal .rc-menu-submenu-open > .rc-menu-submenu-title .rc-menu-submenu-arrow {
      transform: rotate(-90deg);
    }
    
    .rc-menu-horizontal .rc-menu-submenu-arrow:before {
        font: normal normal normal 14px/1 FontAwesome;
        content: '\f0da';
    }
`;

const subMenuTransform = (data: HeaderMenuItem[], currentPath: string) => {
  return data.map((item: HeaderMenuItem) => {
    if (item.children !== null) {
      return (
        <SubMenu
          title={<span className='submenu-title-wrapper'>{item.title}</span>}
          key={item.id}
          popupOffset={[10, 15]}
        >
          {subMenuTransform(item.children, currentPath)}
        </SubMenu>
      );
    } else {
      return (
        <MenuItem
          key={item.id}
          className={item.url === currentPath ? 'header-rc-menu-item' : ''}
        >
          <a
            className='font-size-normal weight-bold'
            href={item.url}
            rel={'noreferrer'}
            target={item.external ? '_blank' : '_self'}
          >
            {item.title}
          </a>
        </MenuItem>
      );
    }
  });
};

const getMenuWithVisibleItems = (
  menu: HeaderMenu,
  hiddenMenuItems: HeaderMenuItem[]
) => {
  const visibleItems = menu.items.filter((item: HeaderMenuItem) => {
    return !hiddenMenuItems.find((hiddenItem: HeaderMenuItem) => {
      return hiddenItem.id === item.id;
    });
  });

  return { ...menu, items: [...visibleItems] };
};

interface Props {
  menuItemHoverColor: string;
  menuFontHoverColor: string;
  menuDropdownFontHoverColor: string;
  menuDropdownFontColor: string;
  menuFontColor: string;
  menuDropdownBackgroundColor: string;
  menuBackgroundColor: string;
  menu: HeaderMenu;
  mode: MenuMode;
  openAnimation: string | Record<string, any>;
  defaultOpenKeys?: string[];
  setNavMenuOverflowing: (x: boolean) => void;
  isMobileWidth?: boolean;
  setIsMobileWidth: (x: boolean) => void;
  headerContainerRef: RefObject<HTMLDivElement>;
  headerLogoRef: RefObject<HTMLDivElement>;
  headerMenuRef: RefObject<HTMLDivElement>;
  headerRef: RefObject<HTMLElement>;
}

export const V2Menu = (props: Props) => {
  const {
    menuFontColor,
    menuItemHoverColor,
    menuFontHoverColor,
    menuDropdownBackgroundColor,
    menuBackgroundColor,
    menu,
    defaultOpenKeys,
    mode,
    openAnimation,
    setNavMenuOverflowing,
    isMobileWidth,
    setIsMobileWidth,
    headerContainerRef,
    headerLogoRef,
    headerMenuRef,
    headerRef,
    menuDropdownFontHoverColor,
    menuDropdownFontColor
  } = props;

  const [breaks, setBreaks] = useState<number[]>([]);
  const [hiddenMenuItems, setHiddenMenuItems] = useState<HeaderMenuItem[]>([]);
  const [visibleMenu, setVisibleMenu] = useState<HeaderMenu>(menu);
  const currentPath = window.location.pathname;

  useEffect(() => {
    setVisibleMenu(getMenuWithVisibleItems(menu, hiddenMenuItems));
  }, [hiddenMenuItems, menu]);

  useEffect(() => {
    const tempBreaks = breaks;
    let tempHiddenMenuItems = hiddenMenuItems;

    function updateNav() {
      const navPadding = 30;
      const nav = headerContainerRef?.current?.clientWidth
        ? headerContainerRef?.current?.clientWidth - navPadding
        : 0;

      if (
        headerRef?.current?.clientWidth &&
        headerRef.current.clientWidth <= 1024
      ) {
        setIsMobileWidth(true);
        return;
      }
      setIsMobileWidth(false);

      const left = headerLogoRef?.current?.clientWidth ?? 0;
      const right = headerMenuRef?.current?.clientWidth ?? 0;
      const availableSpace = hiddenMenuItems.length === 0 ? nav : nav - 32; // space for dropdown menu button
      const widthTakenUp = right + left;
      const menuOverflowing = widthTakenUp > availableSpace;
      const roomForOneMore = availableSpace > tempBreaks[tempBreaks.length - 1];

      if (menuOverflowing) {
        tempBreaks.push(widthTakenUp);
        const hiddenMenuItem =
          menu?.items[menu?.items?.length - (1 + tempHiddenMenuItems.length)];
        tempHiddenMenuItems = [...tempHiddenMenuItems, hiddenMenuItem];
      } else if (roomForOneMore) {
        // Move the item to the visible list
        tempHiddenMenuItems.pop();
        tempBreaks.pop();
      }

      // Recur if the visible list is still overflowing the nav
      setTimeout(() => {
        if (
          right + left > availableSpace ||
          (right + left < availableSpace &&
            tempBreaks.length > 0 &&
            tempBreaks[tempBreaks.length - 1] < availableSpace)
        ) {
          updateNav();
        }
      }, 0);

      setHiddenMenuItems([...tempHiddenMenuItems]);
      setBreaks([...tempBreaks]);
    }

    window.addEventListener('resize', updateNav);
    window.addEventListener('load', updateNav);

    return () => {
      window.removeEventListener('resize', updateNav);
      window.removeEventListener('load', updateNav);
    };
  }, []);

  useEffect(() => {
    setNavMenuOverflowing(
      menu &&
        !isMobileWidth &&
        visibleMenu?.items?.length !== menu.items?.length
    );
  }, [menu, isMobileWidth, visibleMenu]);

  return (
    <div>
      <GlobalStyle
        menuFontColor={menuFontColor}
        menuBackgroundColor={menuBackgroundColor}
        menuItemHoverColor={menuItemHoverColor}
        menuFontHoverColor={menuFontHoverColor}
        menuDropdownBackgroundColor={menuDropdownBackgroundColor}
        menuDropdownFontHoverColor={menuDropdownFontHoverColor}
        menuDropdownFontColor={menuDropdownFontColor}
      />

      <Menu
        selectedKeys={['0']}
        mode={mode}
        openAnimation={openAnimation}
        defaultOpenKeys={defaultOpenKeys}
        className={'sl-menu'}
      >
        {visibleMenu.items &&
          visibleMenu.items.map((item: HeaderMenuItem) => {
            if (item.children !== null) {
              return (
                <SubMenu
                  title={
                    <span className='submenu-title-wrapper'>{item.title}</span>
                  }
                  key={item.id}
                  popupClassName={'header-rc-menu-popup'}
                >
                  {subMenuTransform(item.children, currentPath)}
                </SubMenu>
              );
            } else {
              return (
                <MenuItem
                  key={item.id}
                  className={
                    item.url === currentPath ? 'header-rc-menu-item' : ''
                  }
                >
                  <a
                    href={item.url}
                    rel={'noreferrer'}
                    target={item.external ? '_blank' : '_self'}
                  >
                    {item.title}
                  </a>
                </MenuItem>
              );
            }
          })}
      </Menu>
    </div>
  );
};
